// src/components/Layout/ThemeSidebar.js
import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Sidebar.module.css';
import EcomBuddhaLogoIcon from '../../assets/icons/buddha.png';
import ThemePanel from './SidebarPanels/ThemePanel';
import FontsPanel from './SidebarPanels/FontsPanel';
import { useFormStep } from '../../context/FormStepContext';

const ThemeSidebar = ({ onThemesToggle, isBulk = false }) => {
    const { isThemesPanelOpen, setIsThemesPanelOpen, selectedTheme, handleThemeSelect, showBothSidebars, setShowBothSidebars, isFontsPanelOpen, setIsFontsPanelOpen } = useFormStep();

    const handleThemesClick = (e) => {
        e.preventDefault();
        const newState = !isThemesPanelOpen;
        
        // Close fonts panel if opening themes panel
        if (newState && isFontsPanelOpen) {
            setIsFontsPanelOpen(false);
        }
        
        // Close main menu (Sidebar) smoothly when opening the themes panel
        if (newState && showBothSidebars) {
            setShowBothSidebars(false);
            // Wait for sidebar to close before opening panel for smooth animation
            setTimeout(() => {
                setIsThemesPanelOpen(newState);
                if (onThemesToggle) {
                    onThemesToggle(newState);
        }
            }, 200); // Half of the sidebar transition duration (400ms / 2)
            } else {
            setIsThemesPanelOpen(newState);
            if (onThemesToggle) {
                onThemesToggle(newState);
            }
        }
    };

    const handleClosePanel = () => {
        setIsThemesPanelOpen(false);
        if (onThemesToggle) {
            onThemesToggle(false);
    }
    };

    const handleMainMenuClick = () => {
        // Close theme panel if open
        if (isThemesPanelOpen) {
            setIsThemesPanelOpen(false);
            if (onThemesToggle) {
                onThemesToggle(false);
            }
        }
        // Close fonts panel if open
        if (isFontsPanelOpen) {
            setIsFontsPanelOpen(false);
        }
        // Toggle both sidebars
        setShowBothSidebars(prev => !prev);
    };

    const handleFontsClick = (e) => {
        e.preventDefault();
        const newState = !isFontsPanelOpen;
        
        // Close themes panel if opening fonts panel
        if (newState && isThemesPanelOpen) {
            setIsThemesPanelOpen(false);
            if (onThemesToggle) {
                onThemesToggle(false);
            }
        }
        
        // Close main menu (Sidebar) smoothly when opening the fonts panel
        if (newState && showBothSidebars) {
            setShowBothSidebars(false);
            // Wait for sidebar to close before opening panel for smooth animation
            setTimeout(() => {
                setIsFontsPanelOpen(newState);
            }, 200);
        } else {
            setIsFontsPanelOpen(newState);
        }
    };

    const handleCloseFontsPanel = () => {
        setIsFontsPanelOpen(false);
    };

    return (
        <>
            <aside className={`${styles.sidebar} ${styles.scrollbarHide} ${showBothSidebars ? styles.sidebarShifted : ''}`}>
                {/* Logo Link - Hide when main menu is open but keep space */}
                <Link 
                    to="/dashboard" 
                    className={styles.logoLink}
                    style={{ visibility: showBothSidebars ? 'hidden' : 'visible' }}
                >
                    <div className={styles.sidebarHeaderContent}>
                    <img 
                        src={EcomBuddhaLogoIcon} 
                        className={styles.logoIcon} 
                        alt="EcomBuddha Logo" 
                    />
                    </div>
                </Link>

                {/* Primary Navigation */}
                <nav className={styles.sidebarNav}>
                   <ul>
                        {/* Main Menu Button */}
                        <li className={styles.navItemVertical}>
                            <button 
                                onClick={handleMainMenuClick}
                                className={`${styles.navIconLink} ${showBothSidebars ? styles.navIconLinkActive : ''}`}
                            >
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.1111 24.1673H6.66667C6.35725 24.1673 6.0605 24.0444 5.84171 23.8256C5.62292 23.6068 5.5 23.3101 5.5 23.0007C5.5 22.6912 5.62292 22.3945 5.84171 22.1757C6.0605 21.9569 6.35725 21.834 6.66667 21.834H19.1111C19.4205 21.834 19.7173 21.9569 19.9361 22.1757C20.1549 22.3945 20.2778 22.6912 20.2778 23.0007C20.2778 23.3101 20.1549 23.6068 19.9361 23.8256C19.7173 24.0444 19.4205 24.1673 19.1111 24.1673ZM26.5 16.0007C26.5 15.6912 26.3771 15.3945 26.1583 15.1757C25.9395 14.9569 25.6428 14.834 25.3333 14.834H6.66667C6.35725 14.834 6.0605 14.9569 5.84171 15.1757C5.62292 15.3945 5.5 15.6912 5.5 16.0007C5.5 16.3101 5.62292 16.6068 5.84171 16.8256C6.0605 17.0444 6.35725 17.1673 6.66667 17.1673H25.3333C25.6428 17.1673 25.9395 17.0444 26.1583 16.8256C26.3771 16.6068 26.5 16.3101 26.5 16.0007ZM20.2778 9.00065C20.2778 8.69123 20.1549 8.39449 19.9361 8.17569C19.7173 7.9569 19.4205 7.83398 19.1111 7.83398H6.66667C6.35725 7.83398 6.0605 7.9569 5.84171 8.17569C5.62292 8.39449 5.5 8.69123 5.5 9.00065C5.5 9.31007 5.62292 9.60682 5.84171 9.82561C6.0605 10.0444 6.35725 10.1673 6.66667 10.1673H19.1111C19.4205 10.1673 19.7173 10.0444 19.9361 9.82561C20.1549 9.60682 20.2778 9.31007 20.2778 9.00065Z" fill="#A4A4A4"/>
                                </svg>
                            </button>
                            <button 
                                onClick={handleMainMenuClick}
                                className={showBothSidebars ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel}
                                style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                Main Menu
                            </button>
                        </li>

                        {/* Themes Button */}
                        <li className={styles.navItemVertical}>
                            <button 
                                onClick={handleThemesClick}
                                className={`${styles.navIconLink} ${isThemesPanelOpen ? styles.navIconLinkActive : ''}`}
                            >
                         <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                         <path d="M26.6926 3.03325C26.336 2.56637 25.8413 2.26439 25.2641 2.20871L20.183 1.49446L10.3765 0.120588C9.19537 -0.0443194 8.0967 0.780206 7.90396 1.98812L7.8215 2.53745L5.04701 2.61991C3.86588 2.67452 2.95995 3.63612 2.95995 4.78942L1.88913 4.98217C0.708005 5.17492 -0.0608557 6.27358 0.130818 7.48149L2.7126 22.3145C2.87751 23.3854 3.78342 24.0996 4.85533 24.0996C4.96563 24.0996 5.10269 24.0996 5.23975 24.0718L19.4957 21.5724L20.7047 21.5446C21.0613 21.5168 21.419 21.4075 21.6931 21.2426L22.5723 21.3529C22.6826 21.3808 22.7918 21.3808 22.9021 21.3808C23.3411 21.3808 23.7812 21.2437 24.1657 20.9685C24.6325 20.6119 24.9623 20.0893 25.0448 19.5122L27.1319 4.62428C27.1865 4.07602 27.0494 3.4999 26.6928 3.03302L26.6926 3.03325ZM10.0464 0.780206C10.1289 0.780206 10.1835 0.780206 10.2659 0.808047L19.7427 2.12624L18.8357 8.52669V8.55453V8.58238L17.2423 19.9545L7.76554 18.6363C7.38112 18.5817 7.02346 18.3889 6.77716 18.0591C6.5298 17.7293 6.44735 17.3449 6.47517 16.9604L8.56223 2.07257C8.70036 1.30264 9.30431 0.780206 10.0464 0.780206ZM5.04678 3.2806L7.71102 3.19814L5.78782 16.8223C5.70536 17.3995 5.87027 17.9755 6.20008 18.4424C6.55668 18.9093 7.0514 19.2113 7.62856 19.267L19.1378 20.8872L5.65179 21.2994C4.82725 21.3273 4.14085 20.6955 4.11407 19.871L3.6472 4.81832C3.61935 3.99379 4.22331 3.33521 5.04678 3.2806ZM5.10246 23.4143C4.27793 23.5513 3.50907 23.0299 3.372 22.2053L0.790217 7.37225C0.65315 6.54771 1.17463 5.77885 1.9992 5.64179L2.9608 5.47688L3.39985 19.9256C3.45446 21.1067 4.41606 22.0126 5.59719 22.0126H5.6518L14.6617 21.7374L5.10246 23.4143ZM24.357 19.432C24.3024 19.8442 24.0818 20.2008 23.7531 20.4203C23.4232 20.6677 23.0388 20.7501 22.6544 20.6955L17.902 20.0359L19.4676 8.99358L25.703 9.87272L24.357 19.432ZM26.4451 4.54325L25.7855 9.18542L19.5501 8.30627L20.4014 2.20792L25.1538 2.86863C25.5382 2.92324 25.8958 3.11599 26.1422 3.44582C26.3906 3.77456 26.4997 4.15884 26.4451 4.54325Z" fill="#A4A4A4" stroke="#A4A4A4" strokeWidth="0.2"/>
                                </svg>
                            </button>
                            <button 
                                onClick={handleThemesClick}
                                className={isThemesPanelOpen ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel}
                                style={{ textDecoration: 'none' }}
                            >
                                Themes
                            </button>
                        </li>

                        {/* Fonts Button */}
                        <li className={styles.navItemVertical}>
                            <button 
                                onClick={handleFontsClick}
                                className={`${styles.navIconLink} ${isFontsPanelOpen ? styles.navIconLinkActive : ''}`}
                            >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.71196 14.3765L24.8003 20.1645C25.2535 20.3271 25.2697 20.9602 24.8328 21.1511V21.1521L20.117 23.2065L25.6146 28.7042C25.8228 28.9123 25.8228 29.2495 25.6146 29.4576L23.1125 31.9598C22.9044 32.1679 22.5672 32.1679 22.3591 31.9598L16.8614 26.4613L14.807 31.1771C14.6184 31.6108 13.9755 31.6097 13.8118 31.1207L8.03245 15.0541C7.87959 14.6302 8.2935 14.2248 8.71196 14.3765ZM23.5991 9.89493H24.6648V8.9496H27.2667V15.6765H26.0113V16.7421H29.5868V15.6765H28.3314V8.9496H30.9332V9.89493H31.9989L32 8.4162C32 8.12241 31.7615 7.88281 31.4666 7.88281L24.1325 7.8839C23.8387 7.8839 23.5991 8.1224 23.5991 8.41729L23.5991 9.89493ZM23.1839 20.7112L9.41243 15.7567L14.367 29.5271C14.5968 29.0002 16.1427 25.3219 16.3097 25.1548C16.5178 24.9467 16.855 24.9467 17.0631 25.1548L22.7354 30.8271L24.4841 29.0784L18.8118 23.4073L18.8129 23.4062C18.5538 23.1471 18.6319 22.6939 18.9766 22.5432L23.1839 20.7112Z" fill="#A4A4A4" stroke="#A4A4A4" strokeWidth="0.2"/>
                            </svg>
                            </button>
                            <button 
                                onClick={handleFontsClick}
                                className={isFontsPanelOpen ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel}
                                style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                Fonts
                            </button>
                        </li>
                    </ul>
                </nav>
            </aside>
            <ThemePanel
              isOpen={isThemesPanelOpen}
              onClose={handleClosePanel}
              selectedTheme={selectedTheme}
              onThemeSelect={handleThemeSelect}
              isLoading={false}
              showBothSidebars={showBothSidebars}
              isBulk={isBulk}
            />
            <FontsPanel 
              isOpen={isFontsPanelOpen} 
              onClose={handleCloseFontsPanel}
              isLoading={false}
              showBothSidebars={showBothSidebars}
            />
        </>
    );
};

export default ThemeSidebar;
